import { Bars3Icon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { useAnalytics } from "../../lib/analytics";
import {
	getBetaTagClassName,
	getEnvironment,
} from "../../lib/utils/environment";
import { BetaFeedbackModal } from "../BetaFeedbackModal";
import { SideMenu } from "../SideMenu";
import styles from "./OnboardingHeader.module.css";

interface OnboardingHeaderProps {
	onBack?: () => void;
	showBackButton?: boolean;
	/**
	 * Hamburger menu visibility. Defaults to true (regular onboarding). The
	 * profile journey segment passes false — the side menu isn't relevant there.
	 */
	showMenuButton?: boolean;
}

export function OnboardingHeader({
	onBack,
	showBackButton = true,
	showMenuButton = true,
}: OnboardingHeaderProps) {
	const { logOnboarding } = useAnalytics();
	const [isBetaFeedbackOpen, setIsBetaFeedbackOpen] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const environment = useMemo(() => getEnvironment(), []);
	const betaBadgeClassName = useMemo(
		() => getBetaTagClassName(environment),
		[environment],
	);

	return (
		<>
			<div className={styles.navBar}>
				<button
					type="button"
					onClick={() => {
						logOnboarding("back_clicked");
						onBack?.();
					}}
					className={styles.backButton}
					aria-label="Go back"
					style={{ visibility: showBackButton ? "visible" : "hidden" }}
				>
					<ChevronLeftIcon className={styles.backIcon} />
				</button>

				<div className={styles.logoWithBeta}>
					<button
						type="button"
						className={styles.logoButton}
						onClick={() => logOnboarding("logo_clicked")}
						aria-label="Era"
					>
						<img
							src="/images/era-text-black.png"
							alt="Era"
							className={styles.navLogo}
						/>
					</button>
					<button
						type="button"
						className={styles[betaBadgeClassName]}
						onClick={() => {
							setIsBetaFeedbackOpen(true);
							logOnboarding("beta_tag_clicked");
						}}
					>
						<div className={styles.betaBadgeText}>Beta</div>
					</button>
				</div>

				{showMenuButton ? (
					<button
						type="button"
						onClick={() => {
							setIsMenuOpen(true);
							logOnboarding("menu_clicked");
						}}
						className={styles.menuButton}
						aria-label="Menu"
					>
						<Bars3Icon className={styles.menuIcon} />
					</button>
				) : (
					// Keep the logo centered (the back button on the left has a fixed
					// width); render an invisible spacer in place of the menu button.
					<span className={styles.menuButton} aria-hidden="true" />
				)}
			</div>

			{/* Beta Feedback Modal */}
			<BetaFeedbackModal
				isOpen={isBetaFeedbackOpen}
				onClose={() => setIsBetaFeedbackOpen(false)}
				context="onboarding"
			/>

			{/* Side Menu - minimal version for onboarding (omitted when the menu
			    button is hidden, e.g. the profile journey segment) */}
			{showMenuButton && (
				<SideMenu
					isOpen={isMenuOpen}
					onClose={() => setIsMenuOpen(false)}
					minimal={true}
					context="onboarding"
				/>
			)}
		</>
	);
}
